/**
 * One liability. The interest rate and the EMI are two views of the same loan,
 * so editing either immediately rewrites the other (rulebook pages 2 and 12) —
 * the field you are not driving carries a "linked" tag to say so.
 */

import React from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { rupee } from "../../../Helpers/portfolioPlanner/format";
import type {
  PlannerLiabilityType,
  PlannerOptions,
} from "../../../Helpers/portfolioPlanner/types";
import {
  BadgeRow,
  MoneyField,
  NumberField,
  SelectField,
  TextField,
  WholeNumberField,
} from "./PlannerFields";
import styles from "./portfolio-planner.module.css";
import type { LiabilityBadges, LiabilityDraft } from "./usePlannerForm";

interface PlannerLiabilityRowProps {
  liability: LiabilityDraft;
  index: number;
  badges: LiabilityBadges;
  options: PlannerOptions;
  onPatch: (patch: Partial<LiabilityDraft>) => void;
  onRemove: () => void;
}

const PlannerLiabilityRow = ({
  liability,
  index,
  badges,
  options,
  onPatch,
  onRemove,
}: PlannerLiabilityRowProps) => {
  const liveBadges: { text: string; tone?: "neutral" | "good" | "warn" | "accent" }[] =
    [];

  if (badges.totalInterest != null) {
    liveBadges.push({
      text: `Total interest ${rupee(badges.totalInterest)}`,
      tone: "warn",
    });
  }
  if (badges.sip10 != null && badges.sip10FV != null) {
    liveBadges.push({
      text: `10%-of-EMI SIP ${rupee(badges.sip10)}/mo → ${rupee(badges.sip10FV)}`,
      tone: badges.cancels ? "good" : "neutral",
    });
    if (badges.catchUpSip != null) {
      liveBadges.push({
        text: `Catch-up for time already passed +${rupee(badges.catchUpSip)}/mo`,
        tone: "accent",
      });
    }
    liveBadges.push({
      text: badges.cancels
        ? "Cancels the interest in full"
        : badges.catchUpSip != null
          ? "Falls short of the total interest even with the catch-up"
          : "Falls short — the plan will size the top-up",
      tone: badges.cancels ? "good" : "warn",
    });
  }
  if (!badges.isComplete) {
    liveBadges.push({
      text: "Needs an amount, a tenure, and either a rate or an EMI",
      tone: "warn",
    });
  }

  return (
    <div className={styles.rowCard}>
      <div className={styles.rowHead}>
        <span className={styles.rowTitle}>
          <i className={styles.rowIndex}>{index + 1}</i>
          {liability.name.trim() || liability.type}
        </span>
        <button type="button" className={styles.rowRemove} onClick={onRemove}>
          <DeleteOutlineIcon style={{ fontSize: 14 }} aria-hidden="true" />
          Remove
        </button>
      </div>

      <div className={styles.grid}>
        <SelectField<PlannerLiabilityType>
          label="Liability type"
          value={liability.type}
          options={options.liabilityType}
          onChange={(value) => onPatch({ type: value })}
        />
        <TextField
          label="Liability name"
          value={liability.name}
          placeholder="e.g. HDFC home loan"
          onChange={(value) => onPatch({ name: value })}
        />
        <MoneyField
          label="Outstanding amount"
          value={liability.outstanding}
          placeholder="20,00,000"
          onChange={(value) => onPatch({ outstanding: value })}
        />
        <WholeNumberField
          label="Remaining tenure — years"
          value={liability.years}
          placeholder="e.g. 15"
          maxLength={2}
          suffix="yr"
          onChange={(value) => onPatch({ years: value })}
        />
        <WholeNumberField
          label="Remaining tenure — months"
          value={liability.months}
          placeholder="0–11"
          suffix="mo"
          hint="12 or more rolls into years."
          onChange={(value) => onPatch({ months: value })}
        />
        <NumberField
          label="Interest rate"
          value={liability.rate}
          placeholder="e.g. 9"
          step={0.01}
          suffix="%"
          tag={liability.linkSource === "emi" ? "linked" : undefined}
          onChange={(value) => onPatch({ rate: value })}
        />
        <MoneyField
          label="EMI"
          value={liability.emi}
          placeholder="auto from rate"
          tag={liability.linkSource === "rate" ? "linked" : undefined}
          onChange={(value) => onPatch({ emi: value })}
        />
        <WholeNumberField
          label="Tenure already passed — years"
          value={liability.elapsedYears}
          placeholder="0 if new"
          maxLength={2}
          suffix="yr"
          onChange={(value) => onPatch({ elapsedYears: value })}
        />
        <WholeNumberField
          label="Tenure already passed — months"
          value={liability.elapsedMonths}
          placeholder="0–11"
          suffix="mo"
          hint="12 or more rolls into years."
          onChange={(value) => onPatch({ elapsedMonths: value })}
        />
        <NumberField
          label="Expected return on the SIP"
          value={liability.returnOverride}
          placeholder="segment default"
          step={0.01}
          suffix="%"
          onChange={(value) => onPatch({ returnOverride: value })}
        />
      </div>

      <BadgeRow badges={liveBadges} />
    </div>
  );
};

export default PlannerLiabilityRow;
